/**
 * Le regole di scelta del percorso: cosa si puo' scegliere in un turno e
 * quando una richiesta e' valida. Le usa la pagina pubblica (per dire subito
 * cosa manca) e la funzione evento-pubblico (che ricontrolla tutto).
 */

import type { Percorso, ProgrammaEvento, RegoleScelta, SessioneEvento } from './tipi.ts';

/** Due sessioni sono la stessa lezione se hanno stesso titolo e stessi relatori (es. Sala A ripetuta). */
export function chiaveLezione(s: Pick<SessioneEvento, 'titolo' | 'relatori'>): string {
  return `${s.titolo.trim().toLowerCase()}|${s.relatori.trim().toLowerCase()}`;
}

export function sessionePerCodice(programma: ProgrammaEvento): Map<string, SessioneEvento> {
  return new Map(programma.sessioni.map((s) => [s.codice, s]));
}

/** Le sessioni fra cui si sceglie in un turno di un giorno (solo quelle sceglibili). */
export function scelteDelTurno(programma: ProgrammaEvento, giorno: string, turno: number): SessioneEvento[] {
  return programma.sessioni.filter((s) => s.giorno === giorno && s.sceglibile && s.turni.includes(turno));
}

/** La sessione scelta in un turno, se esiste ed e' ancora sceglibile. */
export function sceltaDelTurno(
  programma: ProgrammaEvento,
  percorso: Percorso | null | undefined,
  giorno: string,
  turno: number,
): SessioneEvento | null {
  const codice = percorso?.[giorno]?.[String(turno)];
  if (!codice) return null;
  const s = sessionePerCodice(programma).get(codice);
  return s && s.sceglibile ? s : null;
}

/**
 * Le sessioni ancora sceglibili in un turno, tenendo conto delle altre scelte
 * dello stesso giorno: con la regola "una volta al giorno" spariscono le
 * lezioni gia' scelte altrove. La scelta attuale del turno resta sempre.
 */
export function scelteDisponibili(
  programma: ProgrammaEvento,
  regole: RegoleScelta,
  percorso: Percorso | null | undefined,
  giorno: string,
  turno: number,
): SessioneEvento[] {
  const tutte = scelteDelTurno(programma, giorno, turno);
  if (!regole.lezione_una_volta_al_giorno) return tutte;
  const attuale = sceltaDelTurno(programma, percorso, giorno, turno);
  const giaScelte = new Set(
    programma.turni
      .filter((t) => t.n !== turno)
      .map((t) => sceltaDelTurno(programma, percorso, giorno, t.n))
      .filter((s): s is SessioneEvento => s !== null && s.codice !== attuale?.codice)
      .map(chiaveLezione),
  );
  return tutte.filter((s) => s.codice === attuale?.codice || !giaScelte.has(chiaveLezione(s)));
}

/** Mette una scelta: una sessione lunga riempie tutti i suoi turni e libera quelli che sostituisce. */
export function conScelta(programma: ProgrammaEvento, percorso: Percorso, sessione: SessioneEvento): Percorso {
  let libero = percorso;
  for (const n of sessione.turni) libero = senzaScelta(programma, libero, sessione.giorno, n);
  const delGiorno = { ...(libero[sessione.giorno] ?? {}) };
  for (const n of sessione.turni) delGiorno[String(n)] = sessione.codice;
  return { ...libero, [sessione.giorno]: delGiorno };
}

/** Toglie la scelta di un turno; se era una sessione lunga, la toglie da tutti i suoi turni. */
export function senzaScelta(programma: ProgrammaEvento, percorso: Percorso, giorno: string, turno: number): Percorso {
  const delGiorno = { ...(percorso[giorno] ?? {}) };
  const codice = delGiorno[String(turno)];
  const tolta = codice ? sessionePerCodice(programma).get(codice) : undefined;
  for (const n of tolta ? tolta.turni : [turno]) delete delGiorno[String(n)];
  return { ...percorso, [giorno]: delGiorno };
}

/** Quanti turni (di tutti i giorni) ci sono da riempire, e quanti sono pieni. */
export function avanzamento(programma: ProgrammaEvento, percorso: Percorso | null | undefined): { fatti: number; totale: number } {
  let fatti = 0;
  for (const g of programma.giorni) {
    for (const t of programma.turni) if (sceltaDelTurno(programma, percorso, g.codice, t.n)) fatti += 1;
  }
  return { fatti, totale: programma.giorni.length * programma.turni.length };
}

export type EsitoValidazione = { ok: true; percorso: Percorso } | { ok: false; errori: string[] };

const MAX_ERRORI = 10;

/**
 * Controlla una richiesta arrivata dal browser. Non si fida di niente:
 * forma dei dati, codici esistenti, giorno e turno giusti, sessione
 * sceglibile, sessioni lunghe scelte per intero, regole dell'evento.
 * Restituisce il percorso ripulito (solo le chiavi note) o l'elenco dei problemi.
 */
export function validaRichiesta(programma: ProgrammaEvento, regole: RegoleScelta, grezzo: unknown): EsitoValidazione {
  if (typeof grezzo !== 'object' || grezzo === null || Array.isArray(grezzo)) {
    return { ok: false, errori: ['Richiesta non leggibile'] };
  }
  const dati = grezzo as Record<string, unknown>;
  const perCodice = sessionePerCodice(programma);
  const errori: string[] = [];
  const percorso: Percorso = {};

  for (const g of programma.giorni) {
    const grezzoGiorno = dati[g.codice];
    const delGiorno = typeof grezzoGiorno === 'object' && grezzoGiorno !== null && !Array.isArray(grezzoGiorno)
      ? (grezzoGiorno as Record<string, unknown>)
      : {};
    const pulito: Record<string, string> = {};
    const lezioni = new Map<string, string>();

    for (const t of programma.turni) {
      const valore = delGiorno[String(t.n)];
      if (valore === undefined || valore === null || valore === '') {
        if (regole.tutti_i_turni) errori.push(`${g.nome}: manca la scelta del turno ${t.n}`);
        continue;
      }
      const s = typeof valore === 'string' ? perCodice.get(valore) : undefined;
      if (!s || s.giorno !== g.codice || !s.turni.includes(t.n)) {
        errori.push(`${g.nome}, turno ${t.n}: incontro non valido`);
        continue;
      }
      if (!s.sceglibile) {
        errori.push(`${g.nome}, turno ${t.n}: questo incontro non si sceglie nel percorso`);
        continue;
      }
      // una sessione lunga va scelta in tutti i turni che occupa
      const incompleta = s.turni.some((n) => delGiorno[String(n)] !== s.codice);
      if (incompleta) {
        errori.push(`${g.nome}, turno ${t.n}: ${s.titolo} dura piu' turni e va scelto per intero`);
        continue;
      }
      if (regole.lezione_una_volta_al_giorno) {
        const chiave = chiaveLezione(s);
        const gia = lezioni.get(chiave);
        if (gia && gia !== s.codice) {
          errori.push(`${g.nome}, turno ${t.n}: questa lezione e' gia' scelta in un altro turno`);
          continue;
        }
        lezioni.set(chiave, s.codice);
      }
      pulito[String(t.n)] = s.codice;
    }
    if (Object.keys(pulito).length > 0) percorso[g.codice] = pulito;
  }

  if (errori.length > 0) return { ok: false, errori: errori.slice(0, MAX_ERRORI) };
  if (Object.keys(percorso).length === 0) return { ok: false, errori: ['Nessuna scelta'] };
  return { ok: true, percorso };
}

/** Le sessioni distinte di un percorso (una sessione lunga conta una volta). */
export function sessioniDelPercorso(percorso: Percorso | null | undefined): string[] {
  if (!percorso) return [];
  const codici = new Set<string>();
  for (const delGiorno of Object.values(percorso)) for (const c of Object.values(delGiorno)) codici.add(c);
  return [...codici];
}
